/* eslint-disable react/no-unstable-nested-components -- DataTable `cell` callbacks are called as plain functions, never mounted as components. */
/**
 * Sản phẩm & giá › Nhóm máy chủ, Vị trí and Tính thử dịch vụ (ADR-026 §6): where new services
 * are created, the data centres, and the price of a plan line by line with VAT. Adding and editing
 * need `catalog.manage`.
 */
import { CirclePlus, Pencil, Server, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { api } from '../../api';
import type { CycleCode, Location, PlacementGroup, PlacementMember, QuoteLineOut, ServiceQuote } from '../../api/areas/catalog';
import type { ModuleInstance, ModuleSummary } from '../../api/areas/modules';
import { Badge } from '../../components/Badge';
import { Callout, DataTable, Field, Section, type Column } from '../../components/blocks';
import { DataError } from '../../components/DataError';
import { PageHeader, useDocumentTitle } from '../../components/PageHeader';
import { EmptyState, Loading } from '../../components/States';
import { useWrite, WriteError } from '../../components/Write';
import { chosenOptions, codeOk, formatVnd, optionGroupsOf, sortCycles, type OptionPick } from '../../lib/catalog';
import { useAccess } from '../../lib/access';
import { useApi } from '../../lib/useApi';
import { CatalogField, CodeInput, FormShell, NameFields, useSuggestedCode } from './editorKit';
import { LocationEditForm, PlacementGroupForm } from './EditorForms';
import { OptionPicker } from './OptionPicker';
import { MemberFactsTable, memberKeys, RefreshFactsButton, useAccountsText, useMemberFacts } from './moduleKit';
import { useInstanceLabel, useText } from '../modules/ui';
import { ActionDialog, FormFooter, ReasonField, selectClass, type DialogBodyProps } from '../operator/shared';

function useNames() {
  const { t, i18n } = useTranslation('catalog');
  const en = i18n.language.startsWith('en');
  return { t, en, name: (x: { name_vi: string; name_en: string }) => (en ? x.name_en : x.name_vi) };
}

/** `/products?tab=server-groups` */
export function ServerGroupsPage() {
  const { t, name } = useNames();
  useDocumentTitle(t('servers.title'));
  const manage = useAccess().has(['catalog.manage']);
  const groups = useApi((signal) => api.listPlacementGroups({ signal }), []);
  const locations = useApi((signal) => api.listCatalogLocations({ signal }), []);
  // The modules of each branch the groups serve, read once for every card (Cài đặt › Module; `integration.read`).
  const branches = groups.status === 'success' ? [...new Set(groups.data.placement_groups.map((g) => g.branch))] : [];
  const modules = useApi(
    (signal) => Promise.all(branches.map((b) => api.getModuleBranch(b, { signal }).then((r) => [b, r.branch.modules] as const, () => [b, null] as const))).then((rows) => new Map(rows)),
    [branches.join('\n')],
  );
  const locationList = locations.status === 'success' ? locations.data.locations : [];
  const where = (code: string | null) => {
    const l = locations.status === 'success' ? locations.data.locations.find((x) => x.code === code) : undefined;
    return l ? name(l) : (code ?? '—');
  };
  return (
    <>
      <PageHeader
        title={t('servers.title')}
        crumbs={[{ label: t('crumb') }]}
        subtitle={t('servers.subtitle')}
        actions={
          manage ? (
            <ActionDialog label={t('editor.newServerGroup')} icon={CirclePlus} title={t('editor.newServerGroup')} variant="default" wide onChanged={groups.reload}>
              {(p) => <PlacementGroupForm {...p} locations={locationList} />}
            </ActionDialog>
          ) : null
        }
      />
      {groups.status === 'loading' ? <Loading rows={4} /> : null}
      {groups.status === 'error' ? <DataError error={groups.error} onRetry={groups.reload} /> : null}
      {groups.status === 'success' && groups.data.placement_groups.length === 0 ? <EmptyState title={t('servers.empty')} /> : null}
      <div className="grid gap-4">
        {groups.status === 'success'
          ? groups.data.placement_groups.map((g) => (
              <GroupCard key={g.code} group={g} modules={modules.status === 'success' ? (modules.data.get(g.branch) ?? null) : null} manage={manage} where={where} locations={locationList} onChanged={groups.reload} />
            ))
          : null}
      </div>
    </>
  );
}

/** One server group (ADR-032 §2.11): its module, its members with what their servers report, "Làm mới". */
function GroupCard({
  group: g,
  modules,
  manage,
  where,
  locations,
  onChanged,
}: {
  group: PlacementGroup;
  modules: ModuleSummary[] | null;
  manage: boolean;
  where: (code: string | null) => string;
  locations: Location[];
  onChanged: () => void;
}) {
  const { t, name } = useNames();
  const text = useText();
  const [refreshed, setRefreshed] = useState(0);
  const summary = modules?.find((m) => m.manifest.id === g.module) ?? null;
  const manifest = summary?.manifest ?? null;
  const keys = memberKeys(g.members, g.module, false);
  const facts = useMemberFacts(g.branch, manifest, keys, refreshed);
  const module = manifest ? text(manifest.name) : g.module;
  return (
    <Section
      title={name(g)}
      description={[t(`branch.${g.branch}`), module, t(`servers.strategy.${g.strategy}`), `${t('servers.location')}: ${where(g.location_code)}`, t('servers.plans', { count: g.plan_count ?? 0 })].join(' · ')}
      actions={
        <>
          {manifest?.admin_actions.some((a) => a.key === 'discover') ? <RefreshFactsButton branch={g.branch} module={manifest.id} keys={keys} onDone={() => setRefreshed((n) => n + 1)} /> : null}
          {manage ? (
            <>
              <ActionDialog label={t('editor.editServerGroup')} icon={Pencil} title={t('editor.editServerGroupTitle', { group: name(g) })} wide onChanged={onChanged}>
                {(p) => <PlacementGroupForm {...p} group={g} locations={locations} />}
              </ActionDialog>
              <ActionDialog label={t('servers.members')} icon={Server} title={t('servers.membersTitle', { group: name(g) })} wide onChanged={onChanged}>
                {(p) => <MembersForm {...p} group={g} instances={summary?.instances ?? null} />}
              </ActionDialog>
            </>
          ) : null}
        </>
      }
      flush
    >
      <MemberFactsTable branch={g.branch} members={g.members} summary={summary} facts={facts} />
    </Section>
  );
}

/**
 * The servers of a group (ADR-032 §2.4): connections of the group's module only (Cài đặt › Module;
 * without `integration.read` the connection is typed) and, for hosting, a server of the connection
 * from its last report (`hosting.servers`). Set the weight, take a server out of or back into
 * service for new services, or remove it. Services already created stay where they are.
 */
function MembersForm({ close, changed, group, instances }: DialogBodyProps & { group: PlacementGroup; instances: ModuleInstance[] | null }) {
  const { t } = useNames();
  const label = useInstanceLabel();
  const accountsText = useAccountsText();
  const [members, setMembers] = useState<PlacementMember[]>(group.members);
  const [reason, setReason] = useState('');
  const module = group.module;
  const [instance, setInstance] = useState('');
  const [server, setServer] = useState('');
  const [weight, setWeight] = useState('1');
  const newInstance = instance.trim();
  const servers = useApi(
    (signal) => (group.branch === 'hosting' && newInstance ? api.listHostingServers(module, newInstance, { signal }) : Promise.resolve(null)),
    [group.branch, module, newInstance],
  );
  // A server the panel no longer reports (`gone_at`) cannot take a service: not offered.
  const serverList = servers.status === 'success' ? servers.data?.items.filter((s) => !s.gone_at) : undefined;
  const duplicate = members.some((m) => m.instance_key === newInstance && m.server === server.trim());
  const canAdd = /^[a-z0-9][a-z0-9-]{0,39}$/.test(newInstance) && /^\d+$/.test(weight) && Number(weight) <= 1000 && !duplicate;
  const update = (i: number, change: Partial<PlacementMember>) => setMembers(members.map((x, k) => (k === i ? { ...x, ...change } : x)));
  const w = useWrite((b: { members: PlacementMember[]; reason: string }, opts) => api.setPlacementMembers(group.code, { version: group.version, members: b.members, reason: b.reason }, opts));
  return (
    <form
      className="grid gap-4"
      onSubmit={(e) => {
        e.preventDefault();
        void w.run({ members, reason: reason.trim() }).then((r) => r && changed());
      }}
    >
      {w.state.phase === 'done' ? (
        <Callout tone="ok">{t('applied')}</Callout>
      ) : (
        <>
          {members.length === 0 ? <Callout tone="warn">{t('servers.noMembers')}</Callout> : null}
          <ul className="grid gap-2">
            {members.map((m, i) => (
              <li key={`${m.module}/${m.instance_key}/${m.server}/${i}`} className="flex flex-wrap items-center gap-2 text-sm">
                <span className="min-w-40 font-mono text-xs">
                  {m.module} / {m.instance_key}
                  {m.server ? ` / ${m.server}` : ''}
                </span>
                <Input value={String(m.weight)} onChange={(e) => update(i, { weight: Number(e.target.value.replace(/\D/g, '') || 0) })} inputMode="numeric" className="w-20 tabular" aria-label={t('servers.weight')} />
                <label className="flex items-center gap-1">
                  <input type="checkbox" checked={m.accepting} onChange={(e) => update(i, { accepting: e.target.checked })} />
                  {m.accepting ? t('delivery.accepting') : t('delivery.notAccepting')}
                </label>
                <Button type="button" variant="ghost" size="sm" onClick={() => setMembers(members.filter((_, k) => k !== i))} aria-label={t('servers.remove')}>
                  <Trash2 aria-hidden="true" />
                </Button>
              </li>
            ))}
          </ul>
          <fieldset className="grid gap-3 rounded-md border p-3">
            <legend className="px-1 text-sm font-medium">{t('servers.add')}</legend>
            <Field label={t('servers.instance')}>
              {instances ? (
                <select
                  className={selectClass}
                  value={newInstance}
                  onChange={(e) => {
                    setInstance(e.target.value);
                    setServer('');
                  }}
                >
                  <option value="">{t('servers.chooseConnection')}</option>
                  {instances.map((i) => (
                    <option key={i.key} value={i.key}>
                      {`${label(i)} (${i.key})`}
                    </option>
                  ))}
                </select>
              ) : (
                <Input value={instance} onChange={(e) => setInstance(e.target.value.toLowerCase())} className="font-mono" />
              )}
            </Field>
            <div className="grid gap-3 sm:grid-cols-[1fr_6rem_auto] sm:items-end">
              <Field label={t('servers.server')} hint={serverList && serverList.length === 0 ? t('servers.noServerData') : t('servers.serverHint')}>
                {serverList?.length ? (
                  <select className={selectClass} value={server} onChange={(e) => setServer(e.target.value)}>
                    <option value="">{t('connection.anyServer')}</option>
                    {serverList.map((s) => (
                      <option key={s.server} value={s.server}>
                        {`${s.server} · ${accountsText(s.accounts + s.pending, s.max_accounts)}`}
                      </option>
                    ))}
                  </select>
                ) : (
                  <Input value={server} onChange={(e) => setServer(e.target.value)} className="font-mono" />
                )}
              </Field>
              <Field label={t('servers.weight')}>
                <Input value={weight} onChange={(e) => setWeight(e.target.value.replace(/\D/g, ''))} inputMode="numeric" className="tabular" />
              </Field>
              <Button
                type="button"
                variant="outline"
                disabled={!canAdd}
                onClick={() => {
                  setMembers([...members, { module, instance_key: newInstance, server: server.trim(), weight: Number(weight), accepting: true }]);
                  setServer('');
                  setInstance('');
                }}
              >
                <CirclePlus aria-hidden="true" />
                {t('servers.addButton')}
              </Button>
            </div>
            {duplicate ? <p className="text-xs text-muted-foreground">{t('servers.duplicate')}</p> : null}
          </fieldset>
          <ReasonField value={reason} onChange={setReason} />
        </>
      )}
      {w.state.phase === 'error' ? <WriteError error={w.state.error} /> : null}
      <FormFooter busy={w.busy} disabled={!reason.trim()} submit={w.state.phase === 'done' ? undefined : t('save')} onClose={close} />
    </form>
  );
}

/** `/products?tab=locations` */
export function LocationsPage() {
  const { t, name } = useNames();
  useDocumentTitle(t('locations.title'));
  const manage = useAccess().has(['catalog.manage']);
  const result = useApi((signal) => api.listCatalogLocations({ signal }), []);
  const columns: Column<Location>[] = [
    { key: 'name', header: t('locations.name'), cell: (l) => <span className="font-medium">{name(l)}</span> },
    { key: 'city', header: t('locations.city'), cell: (l) => l.city },
    { key: 'code', header: t('locations.code'), cell: (l) => <span className="font-mono text-xs">{l.code}</span> },
    { key: 'active', header: '', cell: (l) => <Badge tone={l.active ? 'ok' : 'neutral'}>{l.active ? t('locations.active') : t('locations.inactive')}</Badge> },
    {
      key: 'edit',
      header: '',
      cell: (l) =>
        manage ? (
          <ActionDialog label={t('editor.editLocation')} icon={Pencil} variant="ghost" title={t('editor.editLocationTitle', { location: name(l) })} onChanged={result.reload}>
            {(p) => <LocationEditForm {...p} location={l} />}
          </ActionDialog>
        ) : null,
    },
  ];
  return (
    <>
      <PageHeader
        title={t('locations.title')}
        crumbs={[{ label: t('crumb') }]}
        subtitle={t('locations.subtitle')}
        actions={
          manage ? (
            <ActionDialog label={t('locations.add')} icon={CirclePlus} title={t('locations.add')} variant="default" onChanged={result.reload}>
              {(p) => <LocationForm {...p} />}
            </ActionDialog>
          ) : null
        }
      />
      <Section flush>
        {result.status === 'loading' ? <Loading rows={4} /> : null}
        {result.status === 'error' ? <DataError error={result.error} onRetry={result.reload} /> : null}
        {result.status === 'success' ? <DataTable columns={columns} rows={result.data.locations} rowKey={(l) => l.code} empty={<EmptyState title={t('locations.empty')} />} /> : null}
      </Section>
    </>
  );
}

function LocationForm({ close, changed }: DialogBodyProps) {
  const { code, type: setCode, fromName } = useSuggestedCode(undefined, 40);
  const [vi, setVi] = useState('');
  const [en, setEn] = useState('');
  const [city, setCity] = useState('');
  const w = useWrite((b: { code: string; name_vi: string; name_en: string; city: string; reason: string }, opts) => api.createLocation(b, opts));
  const valid = codeOk(code, 40);
  return (
    <FormShell
      write={w}
      close={close}
      disabled={!valid || !vi.trim() || !city.trim()}
      onSubmit={(reason) => void w.run({ code, name_vi: vi.trim(), name_en: (en || vi).trim(), city: city.trim(), reason }).then((r) => r && changed())}
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <NameFields
          vi={vi}
          en={en}
          setVi={(v) => {
            setVi(v);
            fromName(v);
          }}
          setEn={setEn}
        />
        <CatalogField name="locationCode">
          <CodeInput value={code} onChange={setCode} locked={false} valid={valid} />
        </CatalogField>
        <CatalogField name="locationCity">
          <Input value={city} onChange={(e) => setCity(e.target.value)} required />
        </CatalogField>
      </div>
    </FormShell>
  );
}

/** `/products?tab=service-quote` */
export function ServiceQuotePage() {
  const { t, en, name } = useNames();
  useDocumentTitle(t('quote.title'));
  const products = useApi((signal) => api.listCatalogProducts(null, { signal }), []);
  const [product, setProduct] = useState('');
  const detail = useApi((signal) => (product ? api.getCatalogProduct(product, { signal }) : Promise.resolve(null)), [product]);
  const [plan, setPlan] = useState('');
  const [cycle, setCycle] = useState<CycleCode | ''>('');
  const [action, setAction] = useState<'NEW' | 'RENEW'>('NEW');
  const [promo, setPromo] = useState('');
  const [quote, setQuote] = useState<{ q?: ServiceQuote; error?: unknown } | null>(null);
  const planDetail = useApi((signal) => (plan ? api.getCatalogPlan(plan, { signal }) : Promise.resolve(null)), [plan]);
  const books = planDetail.status === 'success' && planDetail.data ? planDetail.data.price_books : [];
  const [bookPick, setBook] = useState('');
  const book = bookPick || books[0]?.id || '';
  const plans = detail.status === 'success' && detail.data ? detail.data.plans : [];
  const chosen = plans.find((p) => p.id === plan);
  const cycles = chosen ? sortCycles(chosen.cycles) : [];
  // ADR-032 §2.7: the plan's option groups; a group left as it is takes its default.
  const optionGroups = chosen && detail.status === 'success' && detail.data ? optionGroupsOf(detail.data.options, chosen.id) : [];
  const [picks, setPicks] = useState<Record<string, OptionPick>>({});
  const run = () => {
    if (!plan || !cycle || !book) return;
    setQuote(null);
    api.quoteService({ plan_id: plan, cycle, action, promotion_code: promo.trim() || null, price_book_id: book, options: chosenOptions(optionGroups, picks) }).then(
      (q) => setQuote({ q }),
      (error: unknown) => setQuote({ error }),
    );
  };
  const columns: Column<QuoteLineOut>[] = [
    {
      key: 'line',
      header: t('quote.line'),
      cell: (l) => (
        <>
          <span className="font-medium">{en ? l.description_en : l.description_vi}</span>
          <span className="block text-xs text-muted-foreground">
            {t(`quote.kind.${l.kind}`, { defaultValue: l.kind })}
            {Number(l.discount) > 0 ? ` · ${t('quote.discount', { amount: formatVnd(l.discount) })}` : ''}
          </span>
        </>
      ),
    },
    { key: 'amount', header: t('quote.amount'), numeric: true, cell: (l) => formatVnd(l.amount) },
    { key: 'vat', header: t('quote.vat'), numeric: true, cell: (l) => (l.tax_code ? `${t(`tax.${l.tax_code}`, { defaultValue: l.tax_code })} · ${formatVnd(l.tax)}` : t('tax.KCT')) },
    { key: 'total', header: t('quote.total'), numeric: true, cell: (l) => <span className="font-medium">{formatVnd(l.total)}</span> },
  ];
  return (
    <>
      <PageHeader title={t('quote.title')} crumbs={[{ label: t('crumb') }]} subtitle={t('quote.subtitle')} />
      <Section>
        <form
          className="grid gap-3 sm:grid-cols-3"
          onSubmit={(e) => {
            e.preventDefault();
            run();
          }}
        >
          <Field label={t('quote.product')}>
            <select className={selectClass} value={product} onChange={(e) => (setProduct(e.target.value), setPlan(''), setCycle(''), setPicks({}))}>
              <option value="">—</option>
              {products.status === 'success'
                ? products.data.products.map((p) => (
                    <option key={p.id} value={p.id}>
                      {name(p)}
                    </option>
                  ))
                : null}
            </select>
          </Field>
          <Field label={t('quote.plan')}>
            <select className={selectClass} value={plan} onChange={(e) => (setPlan(e.target.value), setCycle(''), setPicks({}))} disabled={!product}>
              <option value="">—</option>
              {plans.map((p) => (
                <option key={p.id} value={p.id}>
                  {name(p)}
                </option>
              ))}
            </select>
          </Field>
          <Field label={t('quote.cycle')}>
            <select className={selectClass} value={cycle} onChange={(e) => setCycle(e.target.value as CycleCode)} disabled={!plan}>
              <option value="">—</option>
              {cycles.map((c) => (
                <option key={c} value={c}>
                  {t(`cycle.${c}`)}
                </option>
              ))}
            </select>
          </Field>
          <Field label={t('quote.book')}>
            <select className={selectClass} value={book} onChange={(e) => setBook(e.target.value)} disabled={!plan}>
              {books.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.code} · {t(`prices.bookKind.${b.applies_to}`)}
                </option>
              ))}
            </select>
          </Field>
          <Field label={t('quote.action')}>
            <select className={selectClass} value={action} onChange={(e) => setAction(e.target.value as 'NEW' | 'RENEW')}>
              <option value="NEW">{t('quote.new')}</option>
              <option value="RENEW">{t('quote.renew')}</option>
            </select>
          </Field>
          <Field label={t('quote.promotion')}>
            <Input value={promo} onChange={(e) => setPromo(e.target.value.toUpperCase())} className="font-mono" />
          </Field>
          {optionGroups.length ? (
            <fieldset className="grid gap-3 rounded-md border p-3 sm:col-span-3 sm:grid-cols-3">
              <legend className="px-1 text-sm font-medium">{t('order.options')}</legend>
              <OptionPicker groups={optionGroups} picks={picks} onChange={setPicks} />
            </fieldset>
          ) : null}
          <div className="flex items-end">
            <Button type="submit" disabled={!plan || !cycle || !book}>
              {t('quote.run')}
            </Button>
          </div>
        </form>
      </Section>
      {!plan ? <p className="mt-4 text-sm text-muted-foreground">{t('quote.pick')}</p> : null}
      {quote?.error ? (
        <div className="mt-4">
          <WriteError error={quote.error} />
        </div>
      ) : null}
      {quote?.q ? (
        <Section flush className="mt-4">
          <DataTable columns={columns} rows={quote.q.lines} rowKey={(l) => `${l.kind}:${l.code}`} />
          <dl className="grid gap-1 px-5 py-4 text-sm sm:grid-cols-[1fr_auto]">
            <dt className="text-muted-foreground">{t('quote.subtotal')}</dt>
            <dd className="tabular text-right">{formatVnd(quote.q.subtotal.amount)} đ</dd>
            <dt className="text-muted-foreground">{t('quote.vat')}</dt>
            <dd className="tabular text-right">{formatVnd(quote.q.tax.amount)} đ</dd>
            <dt className="font-semibold">{t('quote.payToday')}</dt>
            <dd className="tabular text-right font-semibold">{formatVnd(quote.q.total.amount)} đ</dd>
            <dt className="text-muted-foreground">{t('quote.renewal')}</dt>
            <dd className="tabular text-right">{formatVnd(quote.q.renewal_subtotal.amount)} đ</dd>
          </dl>
        </Section>
      ) : null}
    </>
  );
}
